import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  MultiSelect,
  Loader,
  Center,
  Badge,
  CloseButton,
} from "@mantine/core";
import { IconSearch, IconSettings, IconX } from "@tabler/icons-react";
import WorkflowBoard from "./WorkflowBoard";
import WorkflowSettings from "./WorkflowSettings";
import WorkflowTaskModal from "./WorkflowTaskModal";
import { getTasks, getColumns } from "../../services/workflowService";
import { getUsers } from "../../services/userService";
import axios from "axios";
import useAuthStore from "../../store/useAuthStore";
import useKeyboardShortcuts from "../../hooks/useKeyboardShortcuts";
import socketService from "../../services/socketService";

const Workflow = () => {
  const [tasks, setTasks] = useState([]);
  const [openedTask, setOpenedTask] = useState(null);
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isNextRefreshSilent, setIsNextRefreshSilent] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    () => localStorage.getItem("workflow_search") || "",
  );
  const [modalOpened, setModalOpened] = useState(false);
  const [createInColumnId, setCreateInColumnId] = useState(null);

  // Filters - load from localStorage
  const [searchQuery, setSearchQuery] = useState(
    () => localStorage.getItem("workflow_search") || "",
  );
  const [selectedPriority, setSelectedPriority] = useState(
    () => localStorage.getItem("workflow_priority") || null,
  );
  const [selectedAssignees, setSelectedAssignees] = useState(() => {
    const saved = localStorage.getItem("workflow_assignees");
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedColumn, setSelectedColumn] = useState(
    () => localStorage.getItem("workflow_column") || null,
  );

  const location = useLocation();
  const navigate = useNavigate();

  // Sync openedTask state when tasks array updates (Socket refresh)
  useEffect(() => {
    console.log(
      `🔄 openedTask sync useEffect: openedTask=${openedTask?.id}, tasks.length=${tasks.length}`,
    );
    if (openedTask) {
      const updated = tasks.find((t) => Number(t.id) === Number(openedTask.id));
      console.log(
        `🔍 openedTask sync check: taskId=${openedTask.id}, found in tasks=${!!updated}`,
      );
      if (updated) {
        const oldJson = JSON.stringify(openedTask);
        const newJson = JSON.stringify(updated);
        const isDifferent = oldJson !== newJson;
        console.log(`📋 Task #${openedTask.id} comparison:`, {
          isDifferent,
          oldTags: openedTask.tags,
          newTags: updated.tags,
          oldPriority: openedTask.priority,
          newPriority: updated.priority,
        });
        if (isDifferent) {
          console.log(`✅ Updating openedTask #${openedTask.id} with new data`);
          setOpenedTask(updated);
        } else {
          console.log(
            `⏭️ openedTask #${openedTask.id} unchanged, skipping update`,
          );
        }
      } else {
        console.log(
          `❌ Task #${openedTask.id} not found in tasks array, closing modal`,
        );
        setOpenedTask(null);
      }
    } else {
      console.log(`⏭️ No openedTask, skipping sync`);
    }
  }, [tasks, openedTask]);

  const searchInputRef = useRef(null);
  const isAdmin = useAuthStore((state) => state.isAdmin);

  const loadData = useCallback(
    async (isInitial = false, isSilent = false) => {
      try {
        console.log("🔄 Loading workflow data...", {
          refreshKey,
          debouncedSearchQuery,
          selectedPriority,
          isSilent,
        });
        if (isInitial) setLoading(true);
        else if (!isSilent) setIsRefreshing(true);

        const [tasksRes, columnsRes, usersRes] = await Promise.all([
          getTasks({
            search: debouncedSearchQuery,
            priority: selectedPriority,
            assignedToId:
              selectedAssignees.length > 0 ? selectedAssignees : undefined,
            columnId: selectedColumn,
          }),
          getColumns(),
          isAdmin
            ? getUsers()
            : axios
                .get(
                  `${process.env.REACT_APP_API_BASE_URL || "http://localhost:9000"}/workflow/assignable-users`,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                  },
                )
                .then((r) => r.data),
        ]);

        console.log("✅ Tasks loaded:", tasksRes.tasks?.length, "tasks");

        if (tasksRes.success) {
          setTasks(tasksRes.tasks);
        }

        if (columnsRes.success) {
          setColumns(columnsRes.columns);
        }

        if (usersRes.success && usersRes.users) {
          console.log(
            "👥 Users loaded:",
            usersRes.users.length,
            "users",
            usersRes.users,
          );
          setUsers(usersRes.users);
        }
      } catch (error) {
        console.error("Error loading workflow data:", error);
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [
      debouncedSearchQuery,
      selectedPriority,
      selectedAssignees,
      selectedColumn,
      refreshKey,
      isAdmin,
    ],
  );

  // Initial load - ONLY ON MOUNT
  useEffect(() => {
    loadData(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle task ID from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get("task");
    if (taskId && tasks.length > 0) {
      const found = tasks.find((t) => Number(t.id) === Number(taskId));
      if (found) {
        setOpenedTask(found);
      }
    }
  }, [location.search, tasks]); // Only sync when tasks are first loaded or changed

  const handleCloseOpenedTask = () => {
    setOpenedTask(null);
    setModalOpened(false);
    setCreateInColumnId(null);
    // Clear task param from URL
    const params = new URLSearchParams(window.location.search);
    params.delete("task");
    navigate({ search: params.toString() }, { replace: true });
  };

  // Filter updates & Manual refreshes (excluding initial load)
  useEffect(() => {
    const isInitial = loading && tasks.length === 0;
    if (!isInitial) {
      loadData(false, isNextRefreshSilent);
      if (isNextRefreshSilent) setIsNextRefreshSilent(false);
    }
  }, [
    refreshKey,
    debouncedSearchQuery,
    selectedPriority,
    selectedAssignees,
    selectedColumn,
    isNextRefreshSilent,
    loadData,
    loading,
    tasks.length,
  ]); // Trigger on any filter change or manual refresh

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem("workflow_search", searchQuery);
    localStorage.setItem("workflow_priority", selectedPriority || "");
    localStorage.setItem(
      "workflow_assignees",
      JSON.stringify(selectedAssignees),
    );
    localStorage.setItem("workflow_column", selectedColumn || "");
  }, [searchQuery, selectedPriority, selectedAssignees, selectedColumn]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onNew: () => {
      // Focus first column's inline create
      console.log("New task shortcut");
    },
    onFocusSearch: () => {
      searchInputRef.current?.focus();
    },
    onEscape: () => {
      if (settingsOpened) {
        setSettingsOpened(false);
      }
    },
  });

  const checkTaskMatchesFilters = useCallback(
    (task) => {
      if (!task) return false;

      // Match Search Query
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        const matchesSearch =
          (task.title || "").toLowerCase().includes(query) ||
          (task.description || "").toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Match Priority
      if (selectedPriority && task.priority !== selectedPriority) return false;

      // Match Assignees (Handles multiple select)
      if (selectedAssignees.length > 0) {
        if (
          !task.assignedToId ||
          !selectedAssignees.includes(task.assignedToId.toString())
        )
          return false;
      }

      // Match Column
      if (selectedColumn && Number(task.columnId) !== Number(selectedColumn))
        return false;

      return true;
    },
    [debouncedSearchQuery, selectedPriority, selectedAssignees, selectedColumn],
  );

  const handleTaskUpdate = useCallback(
    (isSilent = false, optimisticTasks = null, movePayload = null) => {
      // --- LIVE MODE: Surgical Local Updates ---
      if (optimisticTasks) {
        console.log("⚡ Applying Optimistic Update (Local only)");
        setTasks(optimisticTasks);
        return; // No re-fetch
      }

      if (movePayload) {
        console.log(
          `⚡ Applying Live Update (${movePayload.type}), type: ${movePayload.type}`,
        );

        if (movePayload.type === "move") {
          const { taskId, columnId, position } = movePayload;
          // Find the new column info to update the task's column object (for modal header sync)
          const newColumnInfo = columns.find(
            (c) => Number(c.id) === Number(columnId),
          );

          setTasks((prevTasks) => {
            const taskToMove = prevTasks.find(
              (t) => Number(t.id) === Number(taskId),
            );

            // If task exists but now doesn't match filters (rare for move but possible if column filter active)
            if (
              taskToMove &&
              !checkTaskMatchesFilters({ ...taskToMove, columnId })
            ) {
              return prevTasks.filter((t) => Number(t.id) !== Number(taskId));
            }

            // Perform standard move update
            const updatedTasks = prevTasks.map((t) => {
              if (Number(t.id) === Number(taskId)) {
                return {
                  ...t,
                  columnId,
                  position,
                  column: newColumnInfo
                    ? {
                        id: newColumnInfo.id,
                        name: newColumnInfo.name,
                        color: newColumnInfo.color,
                      }
                    : t.column,
                };
              }
              return t;
            });

            updatedTasks.forEach((t) => {
              if (
                Number(t.id) !== Number(taskId) &&
                Number(t.columnId) === Number(columnId) &&
                t.position >= position
              ) {
                t.position += 1;
              }
            });

            return updatedTasks;
          });
        } else if (movePayload.type === "update") {
          const { task: updatedTask } = movePayload;
          const matches = checkTaskMatchesFilters(updatedTask);
          const isCurrentlyOpened =
            openedTask && Number(openedTask.id) === Number(updatedTask.id);

          console.log(
            `🔄 Task update: id=${updatedTask.id}, matches=${matches}, isOpened=${isCurrentlyOpened}`,
          );

          setTasks((prevTasks) => {
            const exists = prevTasks.find(
              (t) => Number(t.id) === Number(updatedTask.id),
            );
            if (exists) {
              if (matches || isCurrentlyOpened) {
                // Update existing task (keep it if it matches filters OR is currently opened)
                console.log(
                  `✅ Updating task #${updatedTask.id} in tasks array`,
                );
                return prevTasks.map((t) =>
                  Number(t.id) === Number(updatedTask.id)
                    ? { ...t, ...updatedTask }
                    : t,
                );
              } else {
                // Was matching, but no longer matches and is not opened
                console.log(
                  `❌ Removing task #${updatedTask.id} from tasks array (doesn't match filters)`,
                );
                return prevTasks.filter(
                  (t) => Number(t.id) !== Number(updatedTask.id),
                );
              }
            } else if (matches || isCurrentlyOpened) {
              // Task newly matches filters OR is currently opened
              console.log(`➕ Adding task #${updatedTask.id} to tasks array`);
              return [...prevTasks, updatedTask];
            }
            return prevTasks;
          });
        } else if (movePayload.type === "delete") {
          const { taskId } = movePayload;
          setTasks((prevTasks) =>
            prevTasks.filter((t) => Number(t.id) !== Number(taskId)),
          );

          // If it's a remote delete (silent === true), we should reload to fix positions
          if (isSilent) {
            console.log(
              `🗑️ Remote delete detected for task #${taskId}. Reloading to sync positions...`,
            );
            loadData(false, true);
          }
        } else if (movePayload.type === "create") {
          const { task: newTask } = movePayload;
          if (checkTaskMatchesFilters(newTask)) {
            setTasks((prevTasks) => {
              if (prevTasks.find((t) => Number(t.id) === Number(newTask.id)))
                return prevTasks;
              return [...prevTasks, newTask];
            });
          }
        }
        return; // No re-fetch!
      }
      // ------------------------------------------

      // Full refresh (e.g. for search or filters)
      console.log(`🔄 Full Workflow refresh (silent: ${isSilent})...`);
      setIsNextRefreshSilent(isSilent);
      setRefreshKey((prev) => prev + 1);
    },
    [checkTaskMatchesFilters, columns, loadData, openedTask],
  );

  // Socket refresh logic moved to top-level for reliability
  useEffect(() => {
    socketService.connect();

    const onBoardRefresh = (data) => {
      console.log("⚡ Real-time board update received (Workflow.js)", data);
      handleTaskUpdate(true, null, data);
    };

    const onBoardColumnsRefresh = () => {
      console.log("⚡ Real-time columns update received");
      handleTaskUpdate(true);
    };

    socketService.onBoardRefresh(onBoardRefresh);
    socketService.onBoardColumnsRefresh(onBoardColumnsRefresh);

    return () => {
      socketService.offBoardRefresh(onBoardRefresh);
      socketService.offBoardColumnsRefresh(onBoardColumnsRefresh);
    };
  }, [handleTaskUpdate]);

  const handleColumnsUpdate = useCallback(() => {
    console.log("🔄 Columns updated, refreshing...");
    setRefreshKey((prev) => prev + 1);
  }, []);

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedPriority(null);
    setSelectedAssignees([]);
    setSelectedColumn(null);
  };

  const handleTaskClick = useCallback((task, columnIdForNew) => {
    console.log(
      `📋 Task clicked: task=${task?.id}, columnIdForNew=${columnIdForNew}`,
    );
    if (task) {
      // Opening existing task
      setOpenedTask(task);
      setModalOpened(true);
    } else {
      // Creating new task in specific column
      setOpenedTask(null);
      setCreateInColumnId(columnIdForNew);
      setModalOpened(true);
    }
  }, []);

  const hasActiveFilters =
    searchQuery ||
    selectedPriority ||
    selectedAssignees.length > 0 ||
    selectedColumn;

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  const userOptions = users.map((user) => ({
    value: user.id.toString(),
    label: user.email,
  }));

  const columnOptions = columns.map((col) => ({
    value: col.id.toString(),
    label: col.name,
  }));

  if (loading) {
    return (
      <Center style={{ height: "80vh" }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <Container size="100%" px={0}>
      <Group justify="space-between" mb="md" px={0}>
        <Title order={2}>Workflow</Title>
        <Group>
          {isAdmin && (
            <Button
              leftSection={<IconSettings size={14} />}
              variant="light"
              onClick={() => setSettingsOpened(true)}
            >
              Settings
            </Button>
          )}
        </Group>
      </Group>

      {/* Filters */}
      <Group mb="md" px={0} wrap="wrap">
        <TextInput
          ref={searchInputRef}
          placeholder="Search tasks... (Press F)"
          leftSection={<IconSearch size={16} />}
          rightSection={isRefreshing ? <Loader size="xs" /> : null}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          placeholder="Priority"
          data={priorityOptions}
          value={selectedPriority}
          onChange={setSelectedPriority}
          clearable
          style={{ width: 150 }}
        />
        <MultiSelect
          placeholder="Assignees"
          data={userOptions}
          value={selectedAssignees}
          onChange={setSelectedAssignees}
          clearable
          style={{ width: 200 }}
        />
        <Select
          placeholder="Column"
          data={columnOptions}
          value={selectedColumn}
          onChange={setSelectedColumn}
          clearable
          style={{ width: 150 }}
        />
        {hasActiveFilters && (
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconX size={14} />}
            onClick={clearAllFilters}
          >
            Clear filters
          </Button>
        )}
      </Group>

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <Group gap="xs" mb="md" px={0}>
          {searchQuery && (
            <Badge
              variant="light"
              rightSection={
                <CloseButton
                  size="xs"
                  onClick={() => setSearchQuery("")}
                  style={{ marginLeft: 4 }}
                />
              }
            >
              Search: {searchQuery}
            </Badge>
          )}
          {selectedPriority && (
            <Badge
              variant="light"
              color={
                selectedPriority === "urgent"
                  ? "red"
                  : selectedPriority === "high"
                    ? "orange"
                    : selectedPriority === "medium"
                      ? "yellow"
                      : "gray"
              }
              rightSection={
                <CloseButton
                  size="xs"
                  onClick={() => setSelectedPriority(null)}
                  style={{ marginLeft: 4 }}
                />
              }
            >
              Priority: {selectedPriority}
            </Badge>
          )}
          {selectedAssignees.map((assigneeId) => {
            const user = users.find((u) => u.id.toString() === assigneeId);
            return user ? (
              <Badge
                key={assigneeId}
                variant="light"
                color="green"
                rightSection={
                  <CloseButton
                    size="xs"
                    onClick={() =>
                      setSelectedAssignees((prev) =>
                        prev.filter((id) => id !== assigneeId),
                      )
                    }
                    style={{ marginLeft: 4 }}
                  />
                }
              >
                Assignee: {user.email}
              </Badge>
            ) : null;
          })}
          {selectedColumn && (
            <Badge
              variant="light"
              color="violet"
              rightSection={
                <CloseButton
                  size="xs"
                  onClick={() => setSelectedColumn(null)}
                  style={{ marginLeft: 4 }}
                />
              }
            >
              Column:{" "}
              {columns.find((c) => c.id.toString() === selectedColumn)?.name}
            </Badge>
          )}
        </Group>
      )}

      <WorkflowBoard
        tasks={tasks}
        columns={columns}
        users={users}
        onTaskUpdate={handleTaskUpdate}
        onTaskClick={handleTaskClick}
      />

      {settingsOpened && (
        <WorkflowSettings
          opened={settingsOpened}
          onClose={() => setSettingsOpened(false)}
          columns={columns}
          users={users}
          onUpdate={handleColumnsUpdate}
        />
      )}

      {modalOpened && (
        <WorkflowTaskModal
          opened={modalOpened}
          onClose={handleCloseOpenedTask}
          task={openedTask}
          columnId={
            createInColumnId ||
            (openedTask ? openedTask.columnId : columns[0]?.id)
          }
          columns={columns}
          users={users}
          onTaskUpdate={handleTaskUpdate}
        />
      )}
    </Container>
  );
};

export default Workflow;
